<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Historie ulic po roce 1989</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/historie_ulic_po_roce_1989.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Malebná ulice na Chodově. / Zdroj: Marek Lašťovka</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Jak se přepisují dějiny v uličním názvosloví Prahy? <br />
                    Článek mapuje vývoj pojmenovávání pražských ulic od roku
                    1989. Ukazuje, že ani jména ulic nejsou vůči dějinám imunní
                    — za každým z nich se skrývá nejen orientační funkce, ale
                    často i politické a kulturní střety.
                </p>

                <p class="font-bold">1989</p>
                <p class="mb-5">
                    Rázný konec dosavadnímu uplatňování tzv. vedoucí úlohy
                    strany v oblasti pražského uličního názvosloví učinila na
                    sklonku roku 1989 „sametová revoluce“. Jako všechny
                    předcházející zásadní politické a ústavní převraty počínaje
                    revolucí 1848 také ona vyvolala a vzápětí prosadila vlnu
                    přejmenování veřejných prostranství. Na jejím počátku stál
                    spontánní všeobecný návrat k názvu
                    <span class="italic">náměstí Jana Palacha</span>
                    ještě v listopadových dnech roku 1989. Růst živelných
                    iniciativ a návrhů na přejmenování vedl již 13. prosince
                    1989 ke vzniku Nezávislé skupiny pro pojmenování ulic a
                    veřejných prostranství při Archivu hlavního města Prahy.
                    Skupina, složená z dobrovolných pracovníků z onomastického
                    oddělení Ústavu pro jazyk český ČSAV, katedry českého jazyka
                    Filozofické fakulty Univerzity Karlovy, Geodetického a
                    kartografického podniku Praha, Archivu hl. města Prahy,
                    Muzea hl. města Prahy, Klubu za starou Prahu, Institutu pro
                    středoevropskou kulturu a politiku, Genealogické a
                    heraldické společnosti, Historického ústavu Československé
                    armády, odboru dopravy a odboru vnitřních věcí NVP, v jejímž
                    čele stanul archivář dr. Bohdan Zilynskyj, si vytkla za úkol
                    zabránit rozvratu orientačního systému města, předejít
                    nežádoucím krajnostem přejmenovávání a vnést nový řád do
                    názvoslovné činnosti. Znamenalo to připravit nové směrnice a
                    zásady pro výběr i schvalování názvů veřejných prostranství,
                    ale zejména se z přísně odborných hledisek a co možná
                    nejobjektivněji vyjadřovat ke všem předkládaným návrhům na
                    pojmenování a přejmenování, ovlivňovat již jejich vznik, a
                    především poskytovat kvalifikovaná stanoviska pro příslušná
                    rozhodnutí rady zastupitelstva hlavního města Prahy.
                </p>

                <p class="font-bold">1989–1996</p>
                <p class="mb-5">
                    Z různých důvodů však příprava i schvalovací proces
                    přejmenovávání postupovaly pomalým tempem a do konce roku
                    1990 byly v Praze změněny pouze necelé čtyři desítky názvů.
                    V kontrastu k pozvolnému projednávání a schvalování nových
                    názvoslovných změn objevila se již na jaře roku 1990 znovu
                    praxe, uplatňovaná v pražském uličním názvosloví opakovaně v
                    jeho předchozích etapách: z „vyšší moci“ a z politických
                    důvodů byla u příležitosti mezistátních návštěv takřka ze
                    dne na den prosazena do pražského uličního názvosloví
                    pojmenování podle
                    <span class="italic">Winstona Churchilla</span>
                    a
                    <span class="italic"> Charlese de Gaulla.</span>
                    Bez ohledu na odlišná doporučení výše uvedené nezávislé
                    skupiny bylo po nich přejmenováno dosavadní náměstí
                    <span class="italic">Antonína Zápotockého </span>
                    a ulice
                    <span class="italic">J. M. Sverdlova.</span>
                    Obdobně nábřeží
                    <span class="italic">Bedřicha Engelse</span>
                    bylo na základě osobního přání prezidenta Václava Havla,
                    vysloveného ve sdělovacích prostředcích, přejmenováno na
                    <span class="italic">Rašínovo nábřeží</span>
                    a dosavadní třída
                    <span class="italic">Obránců míru,</span>
                    pro kterou místní občanské iniciativy navrhovaly návrat ke
                    starému názvu
                    <span class="italic">Belcrediho,</span>
                    dostala podle požadavku Klubu Milady Horákové a
                    Československé strany socialistické jméno
                    <span class="italic">třída Milady Horákové.</span>
                    Až na uvedené výjimky se pojmenovávání ulic v Praze již od
                    počátku roku 1991 řídilo určitými dohodnutými pravidly a
                    názvoslovný systém, který vznikl v „polistopadovém“ období,
                    je vcelku vyváženější a stabilnější, nežli byly systémy
                    předcházející. Kampaň polistopadového přejmenování a
                    systematické revize pražského uličního názvosloví se proti
                    původním předpokladům protáhla až do roku 1996
                </p>

                <p class="font-bold">Od roku 1996</p>
                <p class="mb-10 sm:mb-25">
                    Po roce 1996 dostala činnost místopisné komise pravidelný
                    rytmus a její hlavní náplní se stala především příprava a
                    posuzování návrhů na pojmenování nově vzniklých ulic. Za
                    posledních 30 let přibylo v Praze více než 1500 nejen ulic a
                    náměstí, ale i mostů, parků, tunelů, nábřeží, promenád a
                    alejí. Některá veřejná prostranství také zanikla (většinou
                    proto, že přestala plnit funkci veřejné komunikace, byla
                    změněna novou výstavbou nebo byl jejich název úředně zrušen
                    z dalších důvodů). Množství takto zaniklých veřejných
                    prostranství a zrušených názvů je však ve vztahu k jejich
                    celkovému počtu zanedbatelné. Ulice jsou i v současnosti
                    přejmenovávány, většinou z důvodů technických (rozdělení
                    komunikací přerušených náměstími či křižovatkami, přestavba
                    a změna charakteru dosavadní komunikace). V posledních
                    letech se ale opět několikrát opakovala situace, kdy
                    místopisná komise a další zúčastněné organizační složky byly
                    vystaveny tlakům na přejmenování veřejných prostranství z
                    politických, popř. mezinárodněpolitických důvodů. V
                    některých případech šlo o až agresivní snahy buď umístit
                    někam urychleně jméno určité osobnosti (<span class="italic"
                        >Makuchova lávka, promenáda Pawla Adamowicze, promenáda
                        Anny Politkovské,</span
                    >
                    přejmenování
                    <span class="italic">náměstí Pod kaštany</span>
                    na
                    <span class="italic">náměstí Borise Němcova,</span>
                    přejmenování části ulice
                    <span class="italic">Korunovační</span>
                    na
                    <span class="italic">Ukrajinských hrdinů</span>), nebo
                    naopak nežádoucí pojmenování z uličního názvosloví odstranit
                    (přejmenování
                    <span class="italic">Koněvovy</span>
                    na
                    <span class="italic">Hartigovu</span>). Řešení návrhů a
                    požadavků tohoto druhu je mimořádně obtížné, neboť každé
                    přejmenování obydlené ulice nebo náměstí je spojeno s velkou
                    administrativní zátěží zejména pro místní občany, firmy a
                    instituce; všichni jsou z tohoto důvodu nuceni měnit své
                    osobní i firemní doklady a množství údajů v povinných
                    evidencích. Ani v případě, že veřejné prostranství nemá
                    přidělena žádná čísla popisná a neexistují tam konkrétní
                    adresy, není takový zásah zcela bezproblémový; vynucuje si
                    změnu údajů v informačních systémech, představuje jisté
                    finanční náklady a mimo jiné může vést např. ke ztrátě
                    historického původního tradičního názvu.
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/historie-ulic-1900-1989"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2"
                                >Historie ulic 1900–1989</span
                            >
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/lokalni-nazvoslovne-systemy"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2"
                                >Lokální názvoslovné systémy</span
                            >
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
